import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import AuthLayout from "../components/auth/AuthLayout";
import PasswordStrength from "../components/auth/PasswordStrength";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { setErr("Passwords don't match"); return; }
    setErr(""); setLoading(true);
    try {
      await api.post("/api/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthLayout title="Invalid link" testid="reset-invalid">
        <p className="t-body text-slate2 mb-6">This reset link is malformed. Please request a fresh one.</p>
        <Link to="/forgot-password" className="btn btn-primary btn-block">Request a new link</Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password updated" testid="reset-success">
        <div className="flex flex-col items-center text-center">
          <CheckCircle2 size={48} className="text-emerald2 mb-4" />
          <p className="t-body text-slate2 mb-6">You've been signed out of all devices. Redirecting to sign in…</p>
          <Link to="/login" className="btn btn-primary btn-block">Sign in now</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="It must be at least 12 characters with mixed case, a number, and a symbol." testid="reset-page">
      {err && (
        <div className="mb-4 p-3 rounded-md text-sm flex items-start gap-2" style={{ background: "rgba(239,68,68,0.06)", color: "#991B1B", border: "1px solid #FECACA" }}>
          <AlertCircle size={16} className="mt-0.5" />{err}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label2">New password<span className="req">*</span></label>
          <div className="relative">
            <input type={showPw ? "text" : "password"} required className="input2 pr-12" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate2">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <PasswordStrength password={pw} />
        </div>
        <div>
          <label className="label2">Confirm password<span className="req">*</span></label>
          <input type={showPw ? "text" : "password"} required className="input2" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={loading} data-testid="reset-submit">
          {loading ? <><Loader2 size={16} className="animate-spin" />Updating…</> : "Update password"}
        </button>
      </form>
    </AuthLayout>
  );
}
